import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@d12labs/ui/card";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Skeleton } from "@d12labs/ui/skeleton";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Mic } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { speechCoachApi } from "../api";
import { Recorder } from "../components/recorder";

type ScoreResult = Awaited<ReturnType<typeof speechCoachApi.scoreSession>>;

export function PracticePage() {
	const { drillId } = useParams({ strict: false }) as { drillId: string };
	const [transcript, setTranscript] = useState("");
	const [inlineError, setInlineError] = useState<string | null>(null);
	const [scoreResult, setScoreResult] = useState<ScoreResult | null>(null);

	const drillsQuery = useQuery({
		queryKey: ["speech-coach", "drills"],
		queryFn: () => speechCoachApi.drills(),
	});

	const drills = drillsQuery.data;
	const drill = drills
		? [...drills.curated, ...drills.assigned].find((d) => d.id === drillId)
		: undefined;

	// Multipart upload → transcribe → score chain, via the hand-rolled api entry
	// (multipart isn't a typedRoute shape). Errors surface as thrown apiFetch errors.
	const handleRecordingSubmit = async (blob: Blob, durationSec: number) => {
		const fd = new FormData();
		fd.append("audio", blob, `practice-${drillId}.webm`);
		fd.append("drillId", drillId);
		fd.append("durationSec", String(durationSec));
		await speechCoachApi.sessionAudio(fd);
	};

	const submit = useMutation({
		mutationFn: async () => {
			// Step 1: create session with transcript
			const created = await speechCoachApi.createSession({ input: { drillId, transcript } });
			// Step 2: score the session
			const scored = await speechCoachApi.scoreSession({ params: { id: created.session.id } });
			return scored;
		},
		onSuccess: (data) => {
			setInlineError(null);
			setScoreResult(data);
			const score = Number(data.session.aiScore ?? data.aiOutput.score);
			toast.success(`Scored ${score}/10`);
		},
		onError: (err) => {
			const message = err instanceof Error ? err.message : "Failed to score session";
			setInlineError(message);
			toast.error(message);
		},
	});

	if (drillsQuery.isLoading) {
		return (
			<>
				<PageHeader title="Practice" icon={Mic} />
				<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
					<Skeleton w="100%" h={200} />
				</PageBody>
			</>
		);
	}

	if (!drill) {
		return (
			<>
				<PageHeader title="Practice" icon={Mic} />
				<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
					<EmptyState
						icon={Mic}
						title="Drill not found"
						body="This drill does not exist or is not accessible."
						primary={
							<Button asChild>
								<Link to="/speech-coach">Back to Today</Link>
							</Button>
						}
					/>
				</PageBody>
			</>
		);
	}

	return (
		<>
			<PageHeader
				title={drill.title}
				icon={Mic}
				badges={<Badge tone="primary">{drill.category}</Badge>}
				actions={
					<Button variant="ghost" size="sm" asChild>
						<Link to="/speech-coach">
							<ArrowLeft className="size-4 mr-1" />
							Back
						</Link>
					</Button>
				}
			/>
			<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
				{/* Instructions card */}
				<Card>
					<CardHeader>
						<CardTitle>Instructions</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-ink-muted whitespace-pre-wrap">{drill.instructions}</p>
					</CardContent>
				</Card>

				{/* Success criteria callout */}
				<Card>
					<CardHeader>
						<CardTitle>Success criteria</CardTitle>
					</CardHeader>
					<CardContent className="flex flex-col gap-2">
						<div className="flex items-center gap-2">
							<span className="text-body-sm text-ink-subtle">Target score</span>
							<Badge tone="success">{drill.successCriteria.targetScore}/10</Badge>
						</div>
						<p className="text-body-sm text-ink-subtle">{drill.successCriteria.measurableHint}</p>
					</CardContent>
				</Card>

				{/* Recorder — own Card from inside the component, so no outer wrapper. */}
				{!scoreResult ? (
					<div className="space-y-2">
						<div>
							<h3 className="text-sm font-semibold text-foreground">Record</h3>
							<p className="text-body-sm text-ink-subtle">
								Record a take and submit. Audio scoring lands with WS-B blueprint B3.3.
							</p>
						</div>
						<Recorder onSubmit={handleRecordingSubmit} disabled={submit.isPending} />
					</div>
				) : null}

				{/* Divider between record and paste paths */}
				{!scoreResult ? (
					<div className="flex items-center gap-3">
						<span className="h-px flex-1 bg-hairline" />
						<span className="text-meta uppercase tracking-wide text-ink-subtle">
							Or paste a transcript
						</span>
						<span className="h-px flex-1 bg-hairline" />
					</div>
				) : null}

				{/* Transcript input */}
				{!scoreResult ? (
					<div className="flex flex-col gap-3">
						<Textarea
							className="min-h-[200px]"
							placeholder="Paste your transcript here…"
							value={transcript}
							onChange={(e) => {
								setTranscript(e.target.value);
								setInlineError(null);
							}}
						/>
						{inlineError ? <div className="text-sm text-destructive">{inlineError}</div> : null}
						<Button
							onClick={() => submit.mutate()}
							disabled={submit.isPending || !transcript.trim()}
						>
							{submit.isPending ? "Scoring…" : "Submit transcript"}
						</Button>
					</div>
				) : null}

				{/* Score result */}
				{scoreResult ? (
					<Card data-testid="score-result-card">
						<CardHeader>
							<CardTitle>Your score</CardTitle>
						</CardHeader>
						<CardContent className="flex flex-col gap-5">
							<div className="flex items-center gap-3">
								<span className="text-4xl font-bold text-foreground">
									{scoreResult.aiOutput.score}/10
								</span>
								<Badge tone={scoreResult.aiOutput.passed ? "success" : "subtle"}>
									{scoreResult.aiOutput.passed ? "Passed" : "Try again"}
								</Badge>
							</div>

							{scoreResult.aiOutput.whatWorked.length > 0 ? (
								<div className="flex flex-col gap-2">
									<h3 className="text-sm font-medium text-foreground">What worked</h3>
									<div className="flex flex-wrap gap-2">
										{scoreResult.aiOutput.whatWorked.map((item) => (
											<span
												key={item}
												className="rounded-full border border-semantic-success/25 bg-semantic-success/10 px-2.5 py-1 text-caption text-semantic-success"
											>
												{item}
											</span>
										))}
									</div>
								</div>
							) : null}

							{scoreResult.aiOutput.whatToTry.length > 0 ? (
								<div className="flex flex-col gap-2">
									<h3 className="text-sm font-medium text-foreground">What to try</h3>
									<div className="flex flex-wrap gap-2">
										{scoreResult.aiOutput.whatToTry.map((item) => (
											<span
												key={item}
												className="rounded-full border border-warning/25 bg-warning/10 px-2.5 py-1 text-caption text-warning"
											>
												{item}
											</span>
										))}
									</div>
								</div>
							) : null}

							{scoreResult.aiOutput.feedback ? (
								<blockquote className="border-l-2 border-hairline-strong pl-4 text-body-sm text-ink-subtle italic">
									{scoreResult.aiOutput.feedback}
								</blockquote>
							) : null}

							<Button variant="outline" onClick={() => setScoreResult(null)}>
								Try again
							</Button>
						</CardContent>
					</Card>
				) : null}
			</PageBody>
		</>
	);
}
